import { Injectable } from "@angular/core";
import { CanActivate, ActivatedRouteSnapshot, PRIMARY_OUTLET, Router, RouterStateSnapshot, UrlTree } from "@angular/router";
import { RoutesEnum, RoutingService } from "../routing.service";
import { AccountService } from "@Services/config";
import { CaseSummarySettingsService } from "@Services/case/case-summary-settings.service";
import { CASE_SUMMARY_TABS } from '@CaseSummary/case-summary-tabs';
import { Utils } from '@Utils';
import { map, take, switchMap, from, Observable } from "rxjs";

@Injectable()
export class AccountSettingGuard implements CanActivate {
    constructor(
        private routingService: RoutingService,
        public accountService: AccountService,
        private caseSummarySettingsService: CaseSummarySettingsService,
        private router: Router
    ) {}

    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
        const requiredSetting: string = route.data["requiredSetting"] as string;
        const isEnabled: boolean = route.data["isEnabled"] as boolean;
        const updatedRoute: RoutesEnum = route.data["routeRedirect"] as RoutesEnum || RoutesEnum.dashboard;
        this.caseSummarySettingsService.startCaseLoad(route.params.caseId);

        return from(this.accountService.getTargetAccount(route.params.accountCode, route.params.versionNumber, false)).pipe(
            switchMap(target =>
                this.caseSummarySettingsService.loadAccountSettings(target.id, target.clientId).pipe(
                    take(1),
                    map(accountSettings => {
                        const setting = accountSettings.find(s => s.name === requiredSetting);
                        if (setting && setting.value === 'true' && !isEnabled) {
                            return this.versionRedirect(updatedRoute, route, state);
                        } else if ((Utils.isNullOrWhitespace(setting?.value) || setting?.value === 'false') && isEnabled) {
                            return this.versionRedirect(updatedRoute, route, state);
                        } else {
                            return true;
                        }
                    })
                )
            )
        );
    }

    private versionRedirect(target: RoutesEnum, route: ActivatedRouteSnapshot, state: RouterStateSnapshot): UrlTree {
        const incoming = this.router.parseUrl(state.url);
        const segments = incoming.root.children[PRIMARY_OUTLET]?.segments ?? [];
        const tab = segments[segments.length - 1]?.path;
        const hasTab = CASE_SUMMARY_TABS.some(candidate => candidate.path === tab) &&
            ['summary', 'summaryV2'].includes(segments[segments.length - 2]?.path);

        const suffix = hasTab ? `/${tab}` : '';
        const redirect = this.router.parseUrl(this.routingService.createUrl(target, route.params) + suffix);
        redirect.queryParams = incoming.queryParams;
        redirect.fragment = incoming.fragment;
        return redirect;
    }
}
